import type {
  IAccountBalanceService,
  IAuthorizedUseCase,
  ICouponRepository,
  ICreateCouponUseCase,
  ICreateExternalCouponWagerUseCase,
  ICreateExternalWagerUseCase,
  ICreateWagerUseCase,
  IDatePickerService,
  IDefinitionService,
  IDeleteCouponUseCase,
  IDeleteCouponWalletUseCase,
  IDrawService,
  IDrawSubscriptionService,
  IExamineWagerUseCase,
  IExternalWagerService,
  IGameAnalysisService,
  IGameNewsService,
  IGetCouponSettingsUseCase,
  IGetCouponsUseCase,
  IGetCouponUseCase,
  IGetCouponWalletsUseCase,
  IGetDateDrawsUseCase,
  IGetDrawForecastUseCase,
  IGetDrawResultUseCase,
  IGetDrawUseCase,
  IGetExternalWagerUseCase,
  IGetGameAnalysisUseCase,
  IGetGameNewsUseCase,
  IGetMarketplaceBalanceUseCase,
  IGetMarketplaceGroupsUseCase,
  IGetMarketplaceWagerDetailsUseCase,
  IGetOpenedDrawsUseCase,
  IGetPlayerAccountBalanceUseCase,
  IGetQuickPickSingleRowsUseCase,
  IGetQuickPickUseCase,
  IGetRetailerBalanceUseCase,
  IGetSportEventOddsUseCase,
  IGetSportEventUseCase,
  IGetUserUseCase,
  IGetWagerListUseCase,
  IGetWagerSettingsUseCase,
  IGetWagerUseCase,
  IMarketplaceGroupsService,
  IMarketplaceWagerDetailsService,
  IParseExternalWagerFileUseCase,
  IQueryClientService,
  IQuickPickService,
  IRemoveWagerUseCase,
  IRequestCacheService,
  ISessionStoreRepository,
  ISocketService,
  ISportEventService,
  ISubscribeDrawForecastUseCase,
  ISubscribeDrawUseCase,
  IUpdateCouponBoardUseCase,
  IUpdateCouponSettingsUseCase,
  IUpdateCouponUseCase,
  IUpdateCouponWalletUseCase,
  IUpdateExternalWagerUseCase,
  IUpdateWagerSettingsUseCase,
  IUserService,
  IWagerClientService,
  IWagerService,
  IWalletService,
} from '../domain/entry.js';
import type { ICouponZustandStoreService } from '../infrastructure/entry.browser.js';
import type {
  ICreateCouponController,
  ICreateExternalWagerController,
  ICreateWagerController,
  IDeleteCouponController,
  IExamineWagerController,
  IGetAccountBalanceController,
  IGetCouponController,
  IGetCouponsController,
  IGetCouponSettingsController,
  IGetCouponWalletsController,
  IGetDateDrawsController,
  IGetDrawController,
  IGetDrawResultController,
  IGetExternalWagerController,
  IGetGameAnalysisController,
  IGetGameNewsController,
  IGetMarketplaceBalanceController,
  IGetMarketplaceGroupsController,
  IGetMarketplaceWagerDetailsController,
  IGetOpenedDrawsController,
  IGetQuickPickController,
  IGetQuickPickSingleRowsController,
  IGetRetailerBalanceController,
  IGetSportEventController,
  IGetSportEventOddsController,
  IGetUserController,
  IGetWagerController,
  IGetWagerListController,
  IGetWagerSettingsController,
  IRemoveWagerController,
  IResetCouponController,
  ISubscribeDrawController,
  ISubscribeDrawForecastController,
  IUpdateCouponBoardController,
  IUpdateCouponController,
  IUpdateCouponSettingsController,
  IUpdateCouponWalletController,
  IUpdateExternalWagerController,
  IUpdateWagerSettingsController,
} from '../interface-adapters/entry.browser.js';
import type { IUploadFileController } from '../interface-adapters/entry.js';

export type DiSymbolMapType = {
  // Services
  IDrawService: IDrawService;
  IDrawSubscriptionService: IDrawSubscriptionService;
  ISportEventService: ISportEventService;
  IWagerService: IWagerService;
  IExternalWagerService: IExternalWagerService;
  IWagerClientService: IWagerClientService;
  IDefinitionService: IDefinitionService;
  IWalletService: IWalletService;
  ICouponZustandStoreService: ICouponZustandStoreService;
  IQuickPickService: IQuickPickService;
  ISocketService: ISocketService;
  IGameAnalysisService: IGameAnalysisService;
  IGameNewsService: IGameNewsService;
  IQueryClientService: IQueryClientService;
  IAccountBalanceService: IAccountBalanceService;
  IRequestCacheService: IRequestCacheService;
  IMarketplaceGroupsService: IMarketplaceGroupsService;
  IMarketplaceWagerDetailsService: IMarketplaceWagerDetailsService;
  IUserService: IUserService;
  IDatePickerService: IDatePickerService;

  // Repositories
  ISessionStoreRepository: ISessionStoreRepository;
  ICouponRepository: ICouponRepository;

  // Use Cases
  IUpdateCouponSettingsUseCase: IUpdateCouponSettingsUseCase;
  IGetCouponSettingsUseCase: IGetCouponSettingsUseCase;
  IUpdateWagerSettingsUseCase: IUpdateWagerSettingsUseCase;
  IGetWagerSettingsUseCase: IGetWagerSettingsUseCase;
  IUpdateCouponUseCase: IUpdateCouponUseCase;
  IUpdateCouponBoardUseCase: IUpdateCouponBoardUseCase;
  IGetCouponUseCase: IGetCouponUseCase;
  IGetCouponsUseCase: IGetCouponsUseCase;
  ICreateCouponUseCase: ICreateCouponUseCase;
  IDeleteCouponUseCase: IDeleteCouponUseCase;
  IGetOpenedDrawsUseCase: IGetOpenedDrawsUseCase;
  IGetDrawUseCase: IGetDrawUseCase;
  IGetDrawResultUseCase: IGetDrawResultUseCase;
  IGetDrawForecastUseCase: IGetDrawForecastUseCase;
  ISubscribeDrawForecastUseCase: ISubscribeDrawForecastUseCase;
  ISubscribeDrawUseCase: ISubscribeDrawUseCase;
  IGetQuickPickSingleRowsUseCase: IGetQuickPickSingleRowsUseCase;
  IGetQuickPickUseCase: IGetQuickPickUseCase;
  IGetSportEventUseCase: IGetSportEventUseCase;
  IGetSportEventOddsUseCase: IGetSportEventOddsUseCase;
  ICreateExternalCouponWagerUseCase: ICreateExternalCouponWagerUseCase;
  ICreateWagerUseCase: ICreateWagerUseCase;
  IGetWagerUseCase: IGetWagerUseCase;
  IGetWagerListUseCase: IGetWagerListUseCase;
  IGetExternalWagerUseCase: IGetExternalWagerUseCase;
  IGetGameAnalysisUseCase: IGetGameAnalysisUseCase;
  IGetGameNewsUseCase: IGetGameNewsUseCase;
  IGetPlayerAccountBalanceUseCase: IGetPlayerAccountBalanceUseCase;
  IRemoveWagerUseCase: IRemoveWagerUseCase;
  IGetMarketplaceGroupsUseCase: IGetMarketplaceGroupsUseCase;
  IGetMarketplaceWagerDetailsUseCase: IGetMarketplaceWagerDetailsUseCase;
  IUpdateExternalWagerUseCase: IUpdateExternalWagerUseCase;
  IExamineWagerUseCase: IExamineWagerUseCase;
  IAuthorizedUseCase: IAuthorizedUseCase;
  IGetUserUseCase: IGetUserUseCase;
  ICreateExternalWagerUseCase: ICreateExternalWagerUseCase;
  IParseExternalWagerFileUseCase: IParseExternalWagerFileUseCase;
  IGetMarketplaceBalanceUseCase: IGetMarketplaceBalanceUseCase;
  IGetRetailerBalanceUseCase: IGetRetailerBalanceUseCase;
  IUpdateCouponWalletUseCase: IUpdateCouponWalletUseCase;
  IDeleteCouponWalletUseCase: IDeleteCouponWalletUseCase;
  IGetCouponWalletsUseCase: IGetCouponWalletsUseCase;
  IGetDateDrawsUseCase: IGetDateDrawsUseCase;

  // Controllers
  IGetUserController: IGetUserController;
  IUpdateCouponSettingsController: IUpdateCouponSettingsController;
  IGetCouponSettingsController: IGetCouponSettingsController;
  IUpdateWagerSettingsController: IUpdateWagerSettingsController;
  IGetWagerSettingsController: IGetWagerSettingsController;
  IUpdateCouponController: IUpdateCouponController;
  IUpdateCouponBoardController: IUpdateCouponBoardController;
  IGetCouponController: IGetCouponController;
  IGetCouponsController: IGetCouponsController;
  ICreateCouponController: ICreateCouponController;
  IDeleteCouponController: IDeleteCouponController;
  IResetCouponController: IResetCouponController;
  IGetOpenedDrawsController: IGetOpenedDrawsController;
  IGetDrawController: IGetDrawController;
  IGetDrawResultController: IGetDrawResultController;
  ISubscribeDrawForecastController: ISubscribeDrawForecastController;
  ISubscribeDrawController: ISubscribeDrawController;
  IGetQuickPickSingleRowsController: IGetQuickPickSingleRowsController;
  IGetQuickPickController: IGetQuickPickController;
  IGetSportEventController: IGetSportEventController;
  IGetSportEventOddsController: IGetSportEventOddsController;
  ICreateExternalWagerController: ICreateExternalWagerController;
  ICreateWagerController: ICreateWagerController;
  IGetWagerController: IGetWagerController;
  IGetExternalWagerController: IGetExternalWagerController;
  IGetWagerListController: IGetWagerListController;
  IGetGameAnalysisController: IGetGameAnalysisController;
  IGetGameNewsController: IGetGameNewsController;
  IGetAccountBalanceController: IGetAccountBalanceController;
  IRemoveWagerController: IRemoveWagerController;
  IGetMarketplaceGroupsController: IGetMarketplaceGroupsController;
  IGetMarketplaceWagerDetailsController: IGetMarketplaceWagerDetailsController;
  IUpdateExternalWagerController: IUpdateExternalWagerController;
  IExamineWagerController: IExamineWagerController;
  IGetMarketplaceBalanceController: IGetMarketplaceBalanceController;
  IGetRetailerBalanceController: IGetRetailerBalanceController;
  IUpdateCouponWalletController: IUpdateCouponWalletController;
  IGetCouponWalletsController: IGetCouponWalletsController;
  IGetDateDrawsController: IGetDateDrawsController;
  IUploadFileController: IUploadFileController;
};

export const diSymbolMap = {
  // Services
  IDrawService: Symbol.for('IDrawService'),
  IDrawSubscriptionService: Symbol.for('IDrawSubscriptionService'),
  ISportEventService: Symbol.for('ISportEventService'),
  IWagerService: Symbol.for('IWagerService'),
  IExternalWagerService: Symbol.for('IExternalWagerService'),
  IWagerClientService: Symbol.for('IWagerClientService'),
  IDefinitionService: Symbol.for('IDefinitionService'),
  IWalletService: Symbol.for('IWalletService'),
  ICouponZustandStoreService: Symbol.for('ICouponZustandStoreService'),
  IQuickPickService: Symbol.for('IQuickPickService'),
  ISocketService: Symbol.for('ISocketService'),
  IGameAnalysisService: Symbol.for('IGameAnalysisService'),
  IGameNewsService: Symbol.for('IGameNewsService'),
  IQueryClientService: Symbol.for('IQueryClientService'),
  IAccountBalanceService: Symbol.for('IAccountBalanceService'),
  IRequestCacheService: Symbol.for('IRequestCacheService'),
  IMarketplaceGroupsService: Symbol.for('IMarketplaceGroupsService'),
  IMarketplaceWagerDetailsService: Symbol.for('IMarketplaceWagerDetailsService'),
  IUserService: Symbol.for('IUserService'),
  IDatePickerService: Symbol.for('IDatePickerService'),

  // Repositories
  ISessionStoreRepository: Symbol.for('ISessionStoreRepository'),
  ICouponRepository: Symbol.for('ICouponRepository'),

  // Use Cases
  IUpdateCouponSettingsUseCase: Symbol.for('IUpdateCouponSettingsUseCase'),
  IGetCouponSettingsUseCase: Symbol.for('IGetCouponSettingsUseCase'),
  IUpdateWagerSettingsUseCase: Symbol.for('IUpdateWagerSettingsUseCase'),
  IGetWagerSettingsUseCase: Symbol.for('IGetWagerSettingsUseCase'),
  IUpdateCouponUseCase: Symbol.for('IUpdateCouponUseCase'),
  IUpdateCouponBoardUseCase: Symbol.for('IUpdateCouponBoardUseCase'),
  IGetCouponUseCase: Symbol.for('IGetCouponUseCase'),
  IGetCouponsUseCase: Symbol.for('IGetCouponsUseCase'),
  ICreateCouponUseCase: Symbol.for('ICreateCouponUseCase'),
  IDeleteCouponUseCase: Symbol.for('IDeleteCouponUseCase'),
  IGetOpenedDrawsUseCase: Symbol.for('IGetOpenedDrawsUseCase'),
  IGetDrawUseCase: Symbol.for('IGetDrawUseCase'),
  IGetDrawResultUseCase: Symbol.for('IGetDrawResultUseCase'),
  IGetDrawForecastUseCase: Symbol.for('IGetDrawForecastUseCase'),
  ISubscribeDrawForecastUseCase: Symbol.for('ISubscribeDrawForecastUseCase'),
  ISubscribeDrawUseCase: Symbol.for('ISubscribeDrawUseCase'),
  IGetQuickPickSingleRowsUseCase: Symbol.for('IGetQuickPickSingleRowsUseCase'),
  IGetQuickPickUseCase: Symbol.for('IGetQuickPickUseCase'),
  IGetSportEventUseCase: Symbol.for('IGetSportEventUseCase'),
  IGetSportEventOddsUseCase: Symbol.for('IGetSportEventOddsUseCase'),
  ICreateExternalCouponWagerUseCase: Symbol.for('ICreateExternalCouponWagerUseCase'),
  ICreateWagerUseCase: Symbol.for('ICreateWagerUseCase'),
  IGetWagerUseCase: Symbol.for('IGetWagerUseCase'),
  IGetExternalWagerUseCase: Symbol.for('IGetExternalWagerUseCase'),
  IGetWagerListUseCase: Symbol.for('IGetWagerListUseCase'),
  IGetGameAnalysisUseCase: Symbol.for('IGetGameAnalysisUseCase'),
  IGetGameNewsUseCase: Symbol.for('IGetGameNewsUseCase'),
  IGetPlayerAccountBalanceUseCase: Symbol.for('IGetPlayerAccountBalanceUseCase'),
  IRemoveWagerUseCase: Symbol.for('IRemoveWagerUseCase'),
  IGetMarketplaceGroupsUseCase: Symbol.for('IGetMarketplaceGroupsUseCase'),
  IGetMarketplaceWagerDetailsUseCase: Symbol.for('IGetMarketplaceWagerDetailsUseCase'),
  IUpdateExternalWagerUseCase: Symbol.for('IUpdateExternalWagerUseCase'),
  IExamineWagerUseCase: Symbol.for('IExamineWagerUseCase'),
  IAuthorizedUseCase: Symbol.for('IAuthorizedUseCase'),
  IGetUserUseCase: Symbol.for('IGetUserUseCase'),
  ICreateExternalWagerUseCase: Symbol.for('ICreateExternalWagerUseCase'),
  IParseExternalWagerFileUseCase: Symbol.for('IParseExternalWagerFileUseCase'),
  IGetMarketplaceBalanceUseCase: Symbol.for('IGetMarketplaceBalanceUseCase'),
  IGetRetailerBalanceUseCase: Symbol.for('IGetRetailerBalanceUseCase'),
  IUpdateCouponWalletUseCase: Symbol.for('IUpdateCouponWalletUseCase'),
  IDeleteCouponWalletUseCase: Symbol.for('IDeleteCouponWalletUseCase'),
  IGetCouponWalletsUseCase: Symbol.for('IGetCouponWalletsUseCase'),
  IGetDateDrawsUseCase: Symbol.for('IGetDateDrawsUseCase'),

  // Controllers
  IGetUserController: Symbol.for('IGetUserController'),
  IUpdateCouponSettingsController: Symbol.for('IUpdateCouponSettingsController'),
  IGetCouponSettingsController: Symbol.for('IGetCouponSettingsController'),
  IUpdateWagerSettingsController: Symbol.for('IUpdateWagerSettingsController'),
  IGetWagerSettingsController: Symbol.for('IGetWagerSettingsController'),
  IUpdateCouponController: Symbol.for('IUpdateCouponController'),
  IUpdateCouponBoardController: Symbol.for('IUpdateCouponBoardController'),
  IGetCouponController: Symbol.for('IGetCouponController'),
  IGetCouponsController: Symbol.for('IGetCouponsController'),
  ICreateCouponController: Symbol.for('ICreateCouponController'),
  IDeleteCouponController: Symbol.for('IDeleteCouponController'),
  IResetCouponController: Symbol.for('IResetCouponController'),
  IGetOpenedDrawsController: Symbol.for('IGetOpenedDrawsController'),
  IGetDrawController: Symbol.for('IGetDrawController'),
  IGetDrawResultController: Symbol.for('IGetDrawResultController'),
  ISubscribeDrawForecastController: Symbol.for('ISubscribeDrawForecastController'),
  ISubscribeDrawController: Symbol.for('ISubscribeDrawController'),
  IGetQuickPickSingleRowsController: Symbol.for('IGetQuickPickSingleRowsController'),
  IGetQuickPickController: Symbol.for('IGetQuickPickController'),
  IGetSportEventController: Symbol.for('IGetSportEventController'),
  IGetSportEventOddsController: Symbol.for('IGetSportEventOddsController'),
  ICreateExternalWagerController: Symbol.for('ICreateExternalWagerController'),
  ICreateWagerController: Symbol.for('ICreateWagerController'),
  IGetWagerController: Symbol.for('IGetWagerController'),
  IGetExternalWagerController: Symbol.for('IGetExternalWagerController'),
  IGetWagerListController: Symbol.for('IGetWagerListController'),
  IGetGameAnalysisController: Symbol.for('IGetGameAnalysisController'),
  IGetGameNewsController: Symbol.for('IGetGameNewsController'),
  IGetAccountBalanceController: Symbol.for('IGetAccountBalanceController'),
  IRemoveWagerController: Symbol.for('IRemoveWagerController'),
  IGetMarketplaceGroupsController: Symbol.for('IGetMarketplaceGroupsController'),
  IGetMarketplaceWagerDetailsController: Symbol.for('IGetMarketplaceWagerDetailsController'),
  IUpdateExternalWagerController: Symbol.for('IUpdateExternalWagerController'),
  IExamineWagerController: Symbol.for('IExamineWagerController'),
  IGetMarketplaceBalanceController: Symbol.for('IGetMarketplaceBalanceController'),
  IGetRetailerBalanceController: Symbol.for('IGetRetailerBalanceController'),
  IUpdateCouponWalletController: Symbol.for('IUpdateCouponWalletController'),
  IGetCouponWalletsController: Symbol.for('IGetCouponWalletsController'),
  IGetDateDrawsController: Symbol.for('IGetDateDrawsController'),
  IUploadFileController: Symbol.for('IUploadFileController'),
} as const satisfies { [K in keyof DiSymbolMapType]: symbol };
